// feedback.service.ts
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, BehaviorSubject } from "rxjs";
import { tap, catchError } from "rxjs/operators";
import { AppConfigService } from "./app-config.service";
import { EquivalencyService } from "../shared/services/equivalency.service";

export interface Feedback {
  extraction?: {
    state: string;
    feedback: string;
  };
  evaluation?: {
    state: string;
    feedback: string;
  };
  equivalency?: {
    state: string;
    feedback: string;
  };
}

@Injectable({ providedIn: "root" })
export class FeedbackService {
  private readonly apiUrl: string = "";
  private readonly feedbackSubject = new BehaviorSubject<Feedback>({
    extraction: { state: "", feedback: "" },
    evaluation: { state: "", feedback: "" },
    equivalency: { state: "", feedback: "" },
  });
  feedback$ = this.feedbackSubject.asObservable();
  private readonly showConsoleLogs: boolean = false;
  private readonly finalFeedbackApiUrl: string = "";

  constructor(
    private readonly http: HttpClient,
    private readonly envService: AppConfigService,
    private readonly equivalencySerice: EquivalencyService,
  ) {
    this.apiUrl = envService.feedbackApiUrl;
    this.showConsoleLogs = this.envService.showConsoleLogs;
    this.finalFeedbackApiUrl = this.envService.mmpFinalFeedbackApiUrl;
  }

  sendFeedback(payload: any): Observable<any> {
    return this.http.post(this.apiUrl, payload);
  }

  setExtractionFeedback(data: Feedback["extraction"]) {
    if (!data) return;
    const current = this.feedbackSubject.value;
    this.feedbackSubject.next({
      ...current,
      extraction: data,
    });
  }

  setEvaluationFeedback(data: Feedback["evaluation"]) {
    if (!data) return;
    this.feedbackSubject.next({
      ...this.feedbackSubject.value,
      evaluation: data,
    });
  }

  setEquivalencyFeedback(data: Feedback["equivalency"]) {
    if (!data) return;
    const current = this.feedbackSubject.value;
    this.feedbackSubject.next({
      ...current,
      equivalency: data,
    });
  }
  getFeedbacks() {
    return this.feedbackSubject.value;
  }

  clearFeedback(): void {
    this.feedbackSubject.next({
      extraction: { state: "", feedback: "" },
      evaluation: { state: "", feedback: "" },
      equivalency: { state: "", feedback: "" },
    });
  }

  sendFinalFeedback(): Observable<any> {
    const current = this.feedbackSubject.value;
    if (this.showConsoleLogs) {
      console.log("Final Feedbacks", current);
    }
    const transcripts = this.equivalencySerice.getEquivalencyData();
    const session_id = transcripts?.transcripts?.length
      ? transcripts.transcripts.map((t) => t.session_key)
      : undefined;
    if (this.showConsoleLogs) {
      console.log("final transcripts", transcripts);
    }
    const payload = {
      session_id: session_id,
      ...current,
    };
    if (this.showConsoleLogs) {
      console.log("Final Feedback Payload", payload);
    }
    return this.http.post(this.finalFeedbackApiUrl, payload).pipe(
      tap(() => {
        if (this.showConsoleLogs) {
          console.log("Feedback successfully sent to API");
        }
      }),
      catchError((error) => {
        if (this.showConsoleLogs) {
          console.error("Error sending feedback to API", error);
        }
        throw error;
      }),
    );
  }
}
